import { Button, Chip } from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridSortDirection,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import { ApiError } from 'common/src/exceptions/ErrorHandling';
import { SingleOperators } from 'common/src/query-params/operators';
import { IUpdateBillDto } from 'common/src/types/bill.dtos';
import { LineItemType } from 'common/src/types/enums/LineItemType.enum';
import { IBill, ILineItem } from 'common/src/types/interfaces';

import { getBill, getBills, updateBill } from 'portal/src/api/BillsService';
import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import {
  customDateFilter,
  customNumberFilter,
} from 'portal/src/components/common/Filters/CustomCollectors';
import { FormCard } from 'portal/src/components/common/forms/FormCard';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import { getFields } from './BillForm.fields';
import BillFormAccountInfo from './BillFormAccountInfo';
import { useBillFormContext } from './BillFormContext';
import BillFormHeader from './BillFormHeader';
import BillFormProvider from './BillFormProvider';

import { formControlErrors } from '../../../common/forms/formControlErrors';
import addDateColumnProps from '../../../common/tables/addDateColumnProps';
import addNumberColumnProps from '../../../common/tables/addNumberColumnProps';
import useSnackbarHandler from './../../../../utils/Snackbar/useSnackbarHandler';
import BillLineItemsSection from './LineItemTable/BillLineItemsSection';

interface PageState {
  data: Partial<IBill>[];
  totalCount: number;
  count: number;
  page: number;
  pageSize: number;
  prev: string;
  next: string;
  mode: 'prev' | 'next';
}

const INITIAL_PAGE_STATE = {
  data: [],
  count: 0,
  totalCount: 0,
  page: 0,
  pageSize: 50,
  prev: '',
  next: '',
  mode: 'next',
} as PageState;

const rowDisabledClassName = 'row-disabled';

const ViewAccountBillsInner = ({
  handleBackAction,
}: {
  handleBackAction: () => void;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const { id: currentBillId } = useParams();
  const { hasSystemAdminAccess, hasReadOnlyAccess } = useAuth();

  const {
    account,
    accountId,
    bill,
    billIsUsedInAllocations,
    getBillPayload,
    headerForm: {
      control,
      formState: { isDirty, isValid: isFormValid },
      getValues,
      setError,
      setValue,
    },
    lineItems,
    setBill,
    validate,
  } = useBillFormContext();

  const lineItemsDirty = bill.lineItems !== lineItems;

  const [pageState, setPageState] = useState<PageState>({
    ...INITIAL_PAGE_STATE,
  });
  const [editBill, setEditBill] = useState<boolean>(false);

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    {} as GridFilterModel,
  );
  const [sortModel, setSortModel] = useState([
    {
      field: 'startDate',
      sort: 'desc' as GridSortDirection,
    },
  ] as GridSortModel);

  const queryClient = useQueryClient();
  const [{ data: bills, isError, isFetching, refetch }] = useQueries({
    queries: [
      {
        queryKey: ['bills'],
        queryFn: async () =>
          await fetchData({
            sorting: sortModel[0],
            pagination: pageState,
            filter: filterModel,
          }),
        initialData: [],
      },
    ],
  });

  const { mutate: billMutation, isPending } = useMutation({
    mutationKey: ['billMutation'],
    mutationFn: update,
    onSuccess: (data) => {
      queryClient.setQueryData(['bills'], (oldData: IBill[]) => {
        const bills = [...oldData];
        if (oldData) {
          const index = bills.findIndex(({ id }) => id === data.id);
          if (index !== -1) {
            bills[index] = { ...data };
          }
        }
        return bills;
      });
    },
  });

  const filterValidator = useCallback(
    (filter) => {
      let valid = false;
      let uniqualityFilterItem;
      const { items = [] } = filter;
      const containEmptyValues = items.filter(
        (i) =>
          i.value === undefined || i.value === null || i.value.length === 0,
      );

      if (!containEmptyValues.length) {
        valid = true;
        for (const item of items) {
          if (!['==', 'in', 'array-contains-any'].includes(item.operator)) {
            if (!uniqualityFilterItem) uniqualityFilterItem = item;
            if (uniqualityFilterItem.field !== item.field) {
              snackbarHandler.error(
                'Filter error: Interval and inequality operators should be applied in same column.',
              );
              valid = false;
              break;
            }
          }
        }
      }
      return valid;
    },
    [snackbarHandler],
  );

  async function update(): Promise<IBill> {
    try {
      const payload = getBillPayload();

      await updateBill(bill.id, payload as IUpdateBillDto);
      const billUpdated = await getBill(bill.id);

      resetEditBill();
      snackbarHandler.success(MESSAGES.BILL.UPDATED);
      return billUpdated;
    } catch (err: any) {
      if (ApiError.compatible(err)) {
        formControlErrors({ setError, getValues }, err);
      } else {
        Logger.captureError(err as Error);
      }
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }

  async function fetchData({
    sorting,
    pagination,
    filter,
  }: {
    sorting: { field: string; sort: 'desc' | 'asc' };
    pagination: PageState;
    filter: GridFilterModel;
  }): Promise<IBill[]> {
    try {
      const { data, meta } = await getBills({
        limit: pagination.pageSize,
        page: pagination.page,
        sort: sorting.field,
        sortDirection: sorting.sort,
        filters: [
          ...(filter.items?.map((i) => ({
            field: i.field,
            operator: i.operator as any,
            value: i.value,
          })) || []),
          {
            field: 'accountId',
            operator: SingleOperators.EQUALS,
            value: accountId,
          },
        ],
      });
      setPageState((old) => ({
        ...old,
        data: data,
        totalCount: meta.totalCount,
      }));
      return data;
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }

  const restorePageState = (initialState: Partial<PageState> = {}): void => {
    setPageState({ ...INITIAL_PAGE_STATE, ...initialState });
  };

  const resetEditBill = (): void => {
    setEditBill(false);
  };

  const handleUpdate = async () => {
    if (isFormValid) {
      await validate(billMutation);
    }
  };

  const formFieldsConfig = getFields({
    setValue,
    getValues,
    id: bill?.id,
    showImageChangeMsg: () =>
      snackbarHandler.info('Bill image change ready to be saved with the bill'),
    billIsUsedInAllocations,
    imageWasSaved: !!bill?.image || !!bill?.statement?.imageLocation,
    billPageNumber: bill?.pageNumber,
    statementId: bill?.statement?.id,
    importFileId: bill?.importFileId,
    hasSystemAdminAccess: hasSystemAdminAccess(),
    isReadOnly: hasReadOnlyAccess(),
  });

  const debouncedFilter = useMemo(() => {
    return debounce((value) => {
      const isValid = filterValidator(value);
      if (isValid) {
        setPageState({ ...pageState, ...{ next: '', prev: '' } });
        refetch();
      }
    }, 500);
  }, [filterValidator, pageState, refetch]);

  const handleFilterModelChange = useCallback(
    (value) => {
      debouncedFilter(value);
    },
    [debouncedFilter],
  );

  const handleDoubleClick = async (id: string) => {
    if (currentBillId === id) {
      return;
    }

    try {
      const billDoc = await getBill(id);
      setBill(billDoc);
      setEditBill(true);
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'startDate',
      flex: 1,
      filterOperators: customDateFilter(),
      sortingOrder: ['asc', 'desc'],
      ...addDateColumnProps('Start Date'),
    },
    {
      field: 'endDate',
      flex: 1,
      filterOperators: customDateFilter(),
      sortingOrder: ['asc', 'desc'],
      ...addDateColumnProps('End Date'),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      filterOperators: customNumberFilter(),
      sortingOrder: ['asc', 'desc'],
      sortComparator: (v1, v2) =>
        Number(v1 === 'Allocated' ? 0 : v1) -
        Number(v2 === 'Allocated' ? 0 : v2),
      renderCell: (params) =>
        params.row.isAllocated ? 'Allocated' : numberFormatter(params.value),
    },
    {
      field: 'budgetPeriod',
      flex: 1,
      sortable: false,
      filterable: false,
      ...addDateColumnProps('Budget Period', ...[,], 'MMMM yyyy'),
    },
    {
      field: 'lineItems',
      flex: 1,
      filterable: false,
      sortable: false,
      valueGetter: (value: Partial<ILineItem>[]) =>
        value
          .filter(({ type }) => type === LineItemType.Usage)
          .reduce((item, val) => item + val.usage, 0),
      ...addNumberColumnProps('Bill Usage'),
    },
    ...(currentBillId
      ? [
          {
            field: 'id',
            headerName: '',
            align: 'center',
            headerAlign: 'center',
            flex: 0.7,
            minWidth: 120,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => {
              return row.id === currentBillId ? (
                <Chip label="Viewing Bill" variant="outlined" color="primary" />
              ) : (
                <></>
              );
            },
          } as GridColDef,
        ]
      : []),
  ];

  if (editBill) {
    return (
      <FormContainer
        backButtonlabel="Back To Account Bills"
        control={control}
        header={
          <BillFormHeader
            billEntryMethod={bill.billEntryMethod}
            isPaid={bill.isPaid}
          />
        }
        handleBackAction={resetEditBill}
        handleSubmit={async () => await handleUpdate()}
        isSubmitDisabled={hasReadOnlyAccess()}
        isDirty={isDirty || lineItemsDirty}
        isNew={false}
        isPending={isPending}
        beforeMainFields={
          <BillFormAccountInfo
            account={account}
            billUsedInAllocation={
              !!bill.isAllocated || !!bill.allocatedBills?.length
            }
          />
        }
        mainFieldsConfig={formFieldsConfig}
        afterMainFields={<BillLineItemsSection />}
      />
    );
  }

  return (
    <FormCard
      title={`Bills For Account: ${account.number ?? '-'}`}
      showContentDivider={false}
    >
      <DataTable
        actionsConfig={{
          cellClassName: ({ row }) =>
            row.id === currentBillId ? rowDisabledClassName : '',
          readActionConfig: {
            onClick: async (id) => {
              if (currentBillId === id) {
                return;
              }
              await handleDoubleClick(id);
            },
            readHiddenForRow: (row) => row.id === currentBillId,
          },
        }}
        columns={gridColumns}
        checkboxSelection={false}
        dataFetchError={isError}
        disableMultipleColumnsFiltering={true}
        filterMode="server"
        getRowClassName={(row) =>
          row.id === currentBillId ? rowDisabledClassName : ''
        }
        isRowSelectable={({ row }) => row.id !== currentBillId}
        loading={isFetching}
        paginationMode="server"
        paginationModel={{
          page: pageState.page,
          pageSize: pageState.pageSize,
        }}
        onFilterModelChange={(model) => {
          const filterChanges = JSON.stringify(model);
          const filterValues = JSON.stringify(filterModel);

          if (filterChanges === filterValues) {
            return;
          }

          setFilterModel(model);
          handleFilterModelChange(model);
        }}
        onRowDoubleClick={({ id }) => handleDoubleClick(id as string)}
        onPaginationModelChange={(model, { reason }) => {
          if (reason === 'setPaginationModel') {
            const page = model.pageSize !== pageState.pageSize ? 0 : model.page;
            if (model.pageSize !== pageState.pageSize) {
              restorePageState({ pageSize: model.pageSize });
            } else {
              const mode = model.page < pageState.page ? 'prev' : 'next';
              setPageState((old) => ({
                ...old,
                mode,
                page,
                pageSize: model.pageSize,
              }));
            }
            refetch();
          }
        }}
        onSortModelChange={(model) => {
          setSortModel(model);
          setPageState((old) => ({
            ...old,
            page: 0,
            prev: '',
            next: '',
          }));
          refetch();
        }}
        rows={bills}
        rowCount={pageState.totalCount}
        sortingMode="server"
        sortModel={sortModel as any}
        slotProps={{
          filterPanel: {
            logicOperators: [GridLogicOperator.And],
          },
        }}
        sx={{
          [`& .${rowDisabledClassName}`]: {
            backgroundColor: grey[300],
          },
        }}
        toolbarSlotProps={{
          children: (
            <Button
              color="info"
              onClick={handleBackAction}
              size="small"
              sx={{ mx: 1 }}
              variant="outlined"
            >
              Back
            </Button>
          ),
          showFilters: true,
        }}
      />
    </FormCard>
  );
};

const ViewAccountBills = ({
  accountId,
  handleBackAction,
}: {
  accountId: string;
  handleBackAction: () => void;
}) => {
  return (
    <BillFormProvider defaultAccountId={accountId}>
      <ViewAccountBillsInner handleBackAction={handleBackAction} />
    </BillFormProvider>
  );
};

export default ViewAccountBills;
